import { useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { Mail, MapPin, Plus } from "lucide-react";
import Reveal, { EASE } from "@/components/Reveal";
import ContactForm from "@/components/ContactForm";
import { FAQ_ITEMS, EMAIL } from "@/lib/content";

export function About() {
  return (
    <section id="about" className="bg-cream py-28" data-testid="about-section-b">
      <div className="max-w-[1360px] mx-auto px-6 lg:px-12 grid lg:grid-cols-2 gap-16">
        <div className="self-start lg:sticky lg:top-24">
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-forest/55">Meet your coach</p>
            <h2 className="mt-4 font-displayb font-light text-[clamp(2.4rem,5vw,4rem)] leading-[1.05]">
              Akanksha <em className="italic text-clay">Maheshwari</em>
            </h2>
            <blockquote className="mt-9 font-displayb italic font-light text-2xl lg:text-[1.9rem] leading-snug text-forest/90 border-l-2 border-clay pl-6">
              "Support was the missing ingredient all along."
            </blockquote>
          </Reveal>
        </div>
        <div>
          <Reveal className="rounded-[2rem] overflow-hidden aspect-[4/3]">
            <img
              src="/images/akanksha_profile.jpg"
              alt="Akanksha Maheshwari, founder of Well&Truly"
              className="w-full h-full object-cover object-top"
              loading="lazy"
            />
          </Reveal>
          <Reveal delay={0.1}>
            <p className="mt-9 text-forest/75 text-lg leading-relaxed">
              Akanksha founded Well&Truly on a simple belief: most health advice fails not because
              people lack information, but because it ignores real life. Her approach is
              evidence-informed and deeply personal — built around your body, your culture, your
              calendar and your kitchen.
            </p>
            <p className="mt-4 text-forest/75 text-lg leading-relaxed">
              She is a certified health coach, trained in the United Kingdom, and works with clients
              in Dubai and online across the world.
            </p>
          </Reveal>
          <Reveal delay={0.2} className="mt-8 space-y-3.5">
            {[
              "Certified health coach — trained in the UK",
              "Based in Dubai · sessions online & in person",
              "1-on-1 programs, never one-size-fits-all",
            ].map((c) => (
              <p key={c} className="flex items-start gap-3 text-forest/85">
                <span className="w-2 h-2 rounded-full bg-sage mt-2.5 shrink-0" />
                {c}
              </p>
            ))}
          </Reveal>
        </div>
      </div>
    </section>
  );
}

function FaqItem({ i, q, a, open, onToggle }) {
  return (
    <div className="border-b border-forest/15">
      <button data-testid={`faq-item-${i + 1}`} onClick={onToggle} className="w-full flex items-center justify-between gap-6 py-7 text-left">
        <span className="font-displayb text-xl sm:text-2xl">{q}</span>
        <span
          className={`grid place-items-center w-9 h-9 rounded-full border border-forest/25 shrink-0 transition-transform duration-300 ${
            open ? "rotate-45 bg-forest text-cream border-forest" : ""
          }`}
        >
          <Plus size={16} />
        </span>
      </button>
      <AnimatePresence initial={false}>
        {open && (
          <motion.div
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.35, ease: EASE }}
            className="overflow-hidden"
          >
            <p className="pb-7 text-forest/70 max-w-2xl leading-relaxed">{a}</p>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

export function Faq() {
  const [open, setOpen] = useState(0);
  return (
    <section id="faq" className="bg-cream py-28" data-testid="faq-section-b">
      <div className="max-w-[900px] mx-auto px-6">
        <h2
          className="font-displayb font-light text-[clamp(2.2rem,4vw,3.4rem)] text-center"
          data-testid="faq-heading-b"
        >
          Questions, <em className="italic text-clay">answered</em>
        </h2>
        <Reveal className="mt-12 border-t border-forest/15">
          {FAQ_ITEMS.map((f, i) => (
            <FaqItem key={f.q} i={i} q={f.q} a={f.a} open={open === i} onToggle={() => setOpen(open === i ? -1 : i)} />
          ))}
        </Reveal>
      </div>
    </section>
  );
}

const NEXT_STEPS = [
  "Send the form — it takes two minutes.",
  "We meet for a free, relaxed discovery call.",
  "You get a clear recommendation — even if it's not me.",
];

export function Contact() {
  return (
    <section id="contact" className="bg-linen py-28" data-testid="contact-section-b">
      <div className="max-w-[1360px] mx-auto px-6 lg:px-12 grid lg:grid-cols-[0.9fr_1.1fr] gap-16 items-start">
        <div>
          <Reveal>
            <p className="text-xs uppercase tracking-[0.24em] text-forest/55">Take the first step</p>
            <h2 className="mt-4 font-displayb font-light text-[clamp(2.4rem,5vw,4rem)] leading-[1.05]">
              Let's talk about your <em className="italic text-clay">next chapter</em>.
            </h2>
            <p className="mt-6 text-forest/70 max-w-md text-lg leading-relaxed">
              Tell me a little about where you are and where you'd like to be. I read every message
              and reply personally.
            </p>
          </Reveal>
          <Reveal delay={0.1}>
            <ol className="mt-9 space-y-4">
              {NEXT_STEPS.map((s, i) => (
                <li key={s} className="flex items-start gap-4">
                  <span className="font-displayb italic text-clay text-xl w-8 shrink-0">{i + 1}.</span>
                  <span className="text-forest/80 leading-relaxed pt-1">{s}</span>
                </li>
              ))}
            </ol>
          </Reveal>
          <Reveal delay={0.2} className="mt-10 flex flex-col gap-3 text-forest/85">
            <a
              href={`mailto:${EMAIL}`}
              data-testid="contact-email-link"
              className="inline-flex items-center gap-3 hover:text-clay transition-colors"
            >
              <Mail size={18} className="text-clay" /> {EMAIL}
            </a>
            <span className="inline-flex items-center gap-3">
              <MapPin size={18} className="text-clay" /> Dubai, United Arab Emirates · online
              worldwide
            </span>
          </Reveal>
        </div>
        <Reveal delay={0.15} className="bg-cream border border-forest/10 rounded-[2rem] p-8 sm:p-12">
          <ContactForm theme="b" />
        </Reveal>
      </div>
    </section>
  );
}
